import React, { useMemo } from 'react';
import { Flex } from 'antd';

import { NumericScore, Study, StudyScoreClient, TextualResponse, TextualSummary } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExAISummaryCard } from 'jf/components/DevExAISummaryCard';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExTag } from 'jf/components/DevExTag';
import { DevExText } from 'jf/components/DevExText';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { ScoreBlock } from '../ScoreBlock';

import { FreqDistHistogram } from './FreqDistHistogram';
import { useSelectPrompt } from './useSelectPrompt';

type PromptScoreCardProps = {
  score: NumericScore;
  textResponses?: TextualResponse[];
  summary?: TextualSummary;
  promptSlug: string;
  study?: Study;
};

export const PromptScoreCard: React.FC<PromptScoreCardProps> = (props) => {
  const theme = useDevExTheme();
  const prompt = useSelectPrompt({ promptSlug: props.promptSlug });

  const studyPromptRequired = useMemo(() => {
    if (props.study && prompt) {
      const sequencedPrompt = props.study.prompts.find((p) => p.slug === prompt.slug);
      if (sequencedPrompt) {
        return sequencedPrompt.required;
      }
    }
    return true;
  }, [props.study, prompt]);

  const { choiceLabels } = prompt;

  return (
    <DevExCard>
      <Flex gap={theme.variable.spacing.md} justify="space-between">
        <Flex gap={theme.variable.spacing.sm}>
          <div style={{ lineHeight: theme.variable.lineHeight }}>{prompt?.text}</div>
          {!studyPromptRequired && <DevExTag color="yellow">optional</DevExTag>}
        </Flex>
        <Flex vertical gap={theme.variable.spacing.sm}>
          <DevExText type="secondary">Score</DevExText>
          <ScoreBlock score={props.score?.scaledAvg} size="md" />
        </Flex>
      </Flex>
      <FreqDistHistogram prompt={prompt} score={props.score} />
      {choiceLabels && choiceLabels.length > 1 && (
        <Flex
          style={{
            width: '85%',
            marginTop: '-' + theme.variable.spacing.lg,
            alignSelf: 'center',
            fontSize: theme.variable.fontSize.xs,
            color: theme.color.text.secondary,
          }}
          justify="space-between"
        >
          <span>{choiceLabels[0]}</span>
          <span style={{ textAlign: 'right' }}>{choiceLabels[choiceLabels.length - 1]}</span>
        </Flex>
      )}
    </DevExCard>
  );
};

export const PromptTextResponseCard = (props: {
  promptSlug: string;
  responses: TextualResponse[];
  studyRef: string | undefined;
  topicSlug: string | undefined;
}) => {
  const prompt = useSelectPrompt({ promptSlug: props.promptSlug });
  const { data: summary } = useClientQuery(StudyScoreClient.getStudyScoresTextual, {
    studyRef: props.studyRef,
    topicSlug: props.topicSlug,
  });
  return (
    <DevExAISummaryCard
      header={prompt.text}
      summary={summary}
      items={props.responses}
      eventType="responses"
      title="Summary of what respondents said"
    />
  );
};
